import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, Stethoscope, Star, Wallet, Loader2 } from "lucide-react";

const SPECIALIZATIONS = [
  "General Dentist",
  "Endodontist",
  "Prosthodontist",
  "Orthodontist",
  "Oral Surgeon",
  "Periodontist",
  "Pedodontist",
];

// Fallback consultant seed used when /api/consultants is unavailable.
const FALLBACK = [
  { id: "c1", name: "Dr. Ananya Rao", specialization: "Endodontist", experience_years: 12, city: "Mumbai", fee: 800, rating: 4.9 },
  { id: "c2", name: "Dr. Rohan Mehta", specialization: "Orthodontist", experience_years: 9, city: "Bengaluru", fee: 700, rating: 4.8 },
  { id: "c3", name: "Dr. Priya Sharma", specialization: "Prosthodontist", experience_years: 14, city: "New Delhi", fee: 900, rating: 4.9 },
  { id: "c4", name: "Dr. Karthik Menon", specialization: "Oral Surgeon", experience_years: 15, city: "Chennai", fee: 1200, rating: 4.7 },
  { id: "c5", name: "Dr. Nikita Deshpande", specialization: "Periodontist", experience_years: 8, city: "Pune", fee: 600, rating: 4.8 },
  { id: "c6", name: "Dr. Vikram Chauhan", specialization: "Pedodontist", experience_years: 10, city: "Hyderabad", fee: 650, rating: 4.9 },
  { id: "c7", name: "Dr. Sara Iqbal", specialization: "General Dentist", experience_years: 6, city: "Kolkata", fee: 500, rating: 4.7 },
];

export default function Consultation() {
  const [spec, setSpec] = useState("");
  const [consultants, setConsultants] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    let cancelled = false;

    const attempt = async () => {
      const params = new URLSearchParams();
      if (spec) params.set("specialization", spec);

      const finish = (rows) => {
        if (cancelled) return;
        setConsultants(rows);
        setLoading(false);
      };

      const applyGeo = async () => {
        try {
          if (!navigator.geolocation) return;
          await new Promise((resolve) => {
            navigator.geolocation.getCurrentPosition(
              (pos) => {
                params.set("lat", pos.coords.latitude);
                params.set("lng", pos.coords.longitude);
                resolve();
              },
              () => resolve(),
              { timeout: 3000 }
            );
          });
        } catch { /* silent */ }
      };

      await applyGeo();

      try {
        const res = await fetch(`/api/consultants?${params.toString()}`);
        if (res.ok) {
          const data = await res.json();
          const rows = Array.isArray(data) ? data : (data.items || []);
          if (rows.length) {
            finish(rows);
            return;
          }
        }
      } catch { /* fall back */ }

      finish(FALLBACK.filter((c) => !spec || c.specialization === spec));
    };

    attempt();
    return () => { cancelled = true; };
  }, [spec]);

  return (
    <main>
      <section className="bg-gradient-to-br from-[#361D82] via-[#4B2AAD] to-[#6B4CD9] text-white">
        <div className="dyntra-container py-14">
          <p className="text-xs font-bold uppercase tracking-[0.24em] text-white/70 mb-3">Find Me a Consultant</p>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight">Book 1:1 time with a dental expert</h1>
          <p className="mt-3 text-lg text-white/85 max-w-2xl">
            Clinical second opinions, complex case reviews, business & setup guidance — book verified consultants near you.
          </p>
        </div>
      </section>

      <section className="dyntra-container py-8">
        <div className="flex flex-wrap items-center gap-3">
          <label className="text-sm font-semibold text-gray-700">Specialization</label>
          <select
            data-testid="consult-spec"
            value={spec}
            onChange={(e) => setSpec(e.target.value)}
            className="rounded-xl border border-gray-200 px-3 py-2 text-sm bg-white outline-none focus:border-[#4B2AAD]"
          >
            <option value="">All specializations</option>
            {SPECIALIZATIONS.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <span className="text-xs text-gray-500">Location auto-detected when possible</span>
        </div>

        {loading ? (
          <div className="flex justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-[#4B2AAD]" /></div>
        ) : consultants.length === 0 ? (
          <p className="mt-10 text-sm text-gray-500">No consultants available for this specialization yet.</p>
        ) : (
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {consultants.map((c) => <ConsultantCard key={c.id} c={c} />)}
          </div>
        )}
      </section>
    </main>
  );
}

function ConsultantCard({ c }) {
  return (
    <div data-testid={`consultant-${c.id}`} className="dyntra-card p-5 flex flex-col">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="font-display text-lg font-bold text-gray-900">{c.name}</h3>
          <p className="text-sm text-gray-600 inline-flex items-center gap-1.5 mt-0.5">
            <Stethoscope className="h-3.5 w-3.5 text-[#4B2AAD]" /> {c.specialization}
          </p>
          <p className="text-sm text-gray-600 inline-flex items-center gap-1.5 mt-0.5">
            <MapPin className="h-3.5 w-3.5 text-[#4B2AAD]" /> {c.city}
          </p>
        </div>
        {c.rating && (
          <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-600">
            <Star className="h-3.5 w-3.5 fill-amber-500 text-amber-500" /> {Number(c.rating).toFixed(1)}
          </span>
        )}
      </div>
      <div className="flex items-center justify-between mt-3">
        <div>
          <p className="text-xs text-gray-500 uppercase tracking-wider">Experience</p>
          <p className="text-sm font-semibold text-gray-900">{c.experience_years} yrs</p>
        </div>
        <div>
          <p className="text-xs text-gray-500 uppercase tracking-wider">Fee</p>
          <p className="text-sm font-semibold text-gray-900 inline-flex items-center gap-1">
            <Wallet className="h-3.5 w-3.5 text-emerald-600" /> ₹{Number(c.fee || 0).toLocaleString()}
          </p>
        </div>
      </div>
      <Link
        to={`/consultation/book/${c.id}`}
        data-testid={`consult-book-${c.id}`}
        className="dyntra-btn-primary mt-4 justify-center py-2.5 text-sm"
      >
        Book Now
      </Link>
    </div>
  );
}
